import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, MenuItem,
    CircularProgress,
} from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, COMPONENT_COLORS, fadeInUp } from '../results/ResultsDesignTokens';
import {
    AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';

const BUILDING_TYPES = [
    'FullServiceRestaurant', 'Hospital', 'LargeHotel', 'LargeOffice',
    'MediumOffice', 'OutPatient', 'PrimarySchool', 'QuickServiceRestaurant',
    'RetailStandalone', 'RetailStripmall', 'SecondarySchool', 'SmallHotel',
    'SmallOffice', 'Warehouse',
];

const CLIMATE_ZONES = [
    '1A', '2A', '2B', '3A', '3B', '3C', '4A', '4B', '4C',
    '5A', '5B', '6A', '6B', '7A',
];

function buildSyntheticWeather(climateZone) {
    // Simple synthetic: sinusoidal annual temperature curve
    // Baseline temp by zone prefix
    const baselines = { '1': 82, '2': 75, '3': 65, '4': 55, '5': 45, '6': 35, '7': 25 };
    const zonePrefix = climateZone.charAt(0);
    const baseline = baselines[zonePrefix] || 60;
    const amplitude = 20;

    const weather = [];
    for (let h = 0; h < 8760; h++) {
        const dayOfYear = Math.floor(h / 24);
        const hourOfDay = h % 24;
        const seasonal = amplitude * Math.sin((dayOfYear - 80) * 2 * Math.PI / 365);
        const diurnal = 8 * Math.sin((hourOfDay - 6) * 2 * Math.PI / 24);
        weather.push(Math.round((baseline + seasonal + diurnal) * 10) / 10);
    }
    return weather;
}

export default function ForecastTab({ service }) {
    const { forecastResult, loading } = service;

    const [buildingType, setBuildingType] = useState('Warehouse');
    const [climateZone, setClimateZone] = useState('3B');
    const [sqft, setSqft] = useState('50000');
    const [vintage, setVintage] = useState('pre-2004');

    const handleGenerate = () => {
        const weather = buildSyntheticWeather(climateZone);
        service.generateForecast(
            {
                building_type: buildingType,
                climate_zone: climateZone,
                floor_area_sqft: parseFloat(sqft),
                vintage_band: vintage,
            },
            weather
        );
    };

    // Prepare chart data from 8760 → daily averages (365 points)
    const chartData = [];
    if (forecastResult?.components) {
        for (let day = 0; day < 365; day++) {
            const start = day * 24;
            const end = start + 24;
            const entry = { day: day + 1 };

            for (const [comp, values] of Object.entries(forecastResult.components)) {
                const slice = values.slice(start, end);
                entry[comp] = slice.reduce((s, v) => s + v, 0) / slice.length;
            }
            chartData.push(entry);
        }
    }

    const textFieldSx = {
        minWidth: 160,
        '& .MuiOutlinedInput-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.8rem',
            color: COLORS.text.primary,
        },
        '& .MuiInputLabel-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            color: COLORS.text.muted,
        },
        '& .MuiMenuItem-root': {
            fontFamily: vortxTypography.mono,
            fontSize: '0.8rem',
        },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Input form */}
            <Box sx={{
                p: 2.5,
                bgcolor: COLORS.bg.card,
                border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
                borderRadius: 2,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    color: COLORS.text.muted,
                    mb: 2,
                }}>
                    Building Metadata
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <TextField
                        select
                        label="Building Type"
                        value={buildingType}
                        onChange={(e) => setBuildingType(e.target.value)}
                        size="small"
                        sx={textFieldSx}
                    >
                        {BUILDING_TYPES.map(t => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Climate Zone"
                        value={climateZone}
                        onChange={(e) => setClimateZone(e.target.value)}
                        size="small"
                        sx={textFieldSx}
                    >
                        {CLIMATE_ZONES.map(z => (
                            <MenuItem key={z} value={z}>{z}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Floor Area (sqft)"
                        value={sqft}
                        onChange={(e) => setSqft(e.target.value)}
                        size="small"
                        type="number"
                        sx={textFieldSx}
                    />

                    <TextField
                        select
                        label="Vintage"
                        value={vintage}
                        onChange={(e) => setVintage(e.target.value)}
                        size="small"
                        sx={textFieldSx}
                    >
                        <MenuItem value="pre-2004">Pre-2004</MenuItem>
                        <MenuItem value="2004-2013">2004-2013</MenuItem>
                        <MenuItem value="post-2013">Post-2013</MenuItem>
                    </TextField>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleGenerate}
                        disabled={loading}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            bgcolor: COLORS.accent,
                            '&:hover': { bgcolor: alpha(COLORS.accent, 0.8) },
                        }}
                    >
                        {loading ? <CircularProgress size={14} sx={{ mr: 1, color: COLORS.text.primary }} /> : null}
                        Generate Forecast
                    </Button>
                </Box>
            </Box>

            {/* Results chart */}
            {forecastResult && chartData.length > 0 && (
                <Box sx={{
                    p: 2.5,
                    bgcolor: COLORS.bg.card,
                    border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
                    borderRadius: 2,
                    animation: `${fadeInUp} 0.4s ease-out`,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: COLORS.text.muted,
                        mb: 1,
                    }}>
                        Component Load Forecast — {buildingType}, {climateZone}
                    </Typography>

                    {/* Sanity checks */}
                    {forecastResult.sanity_checks && (
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            {Object.entries(forecastResult.sanity_checks).map(([key, value]) => (
                                <Box key={key} sx={{
                                    px: 1,
                                    py: 0.25,
                                    bgcolor: alpha(
                                        value === true || value === 'ok' ? COLORS.efficiency : COLORS.solar,
                                        0.1,
                                    ),
                                    borderRadius: 0.5,
                                }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        color: value === true || value === 'ok' ? COLORS.efficiency : COLORS.solar,
                                    }}>
                                        {key}: {String(value)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Stacked area chart */}
                    <Box sx={{ height: 350 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <defs>
                                    {Object.entries(COMPONENT_COLORS).map(([comp, color]) => (
                                        <linearGradient key={comp} id={`grad_${comp}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={color} stopOpacity={0.6} />
                                            <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <XAxis
                                    dataKey="day"
                                    tick={{ fill: COLORS.text.muted, fontSize: 9, fontFamily: vortxTypography.mono }}
                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                    tickLine={false}
                                    interval={29}
                                    label={{
                                        value: 'Day of Year',
                                        position: 'insideBottom',
                                        offset: -5,
                                        fill: COLORS.text.muted,
                                        fontSize: 9,
                                    }}
                                />
                                <YAxis
                                    tick={{ fill: COLORS.text.muted, fontSize: 9, fontFamily: vortxTypography.mono }}
                                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                    tickLine={false}
                                    label={{
                                        value: 'kW (avg)',
                                        angle: -90,
                                        position: 'insideLeft',
                                        fill: COLORS.text.muted,
                                        fontSize: 9,
                                    }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: COLORS.bg.card,
                                        border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.7rem',
                                    }}
                                    formatter={(value) => `${value.toFixed(1)} kW`}
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.65rem',
                                    }}
                                />
                                {Object.entries(COMPONENT_COLORS).map(([comp, color]) => (
                                    <Area
                                        key={comp}
                                        type="monotone"
                                        dataKey={comp}
                                        stackId="1"
                                        stroke={color}
                                        fill={`url(#grad_${comp})`}
                                        strokeWidth={1.5}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
